// pages/admin/AdminNotifications.jsx
import { useAuth } from '../../utils/auth';
import { useState, useEffect, useCallback } from 'react';
import { notifs } from '../../utils/api';
import { Save, Send, CheckCircle2, XCircle, Clock, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const VARS = [
  '{{customer_name}}','{{customer_email}}','{{room_name}}',
  '{{session_date}}','{{session_time}}','{{player_count}}',
  '{{booking_ref}}','{{total_amount}}','{{cancellation_details}}',
];

const STATUS_CONFIG = {
  sent:    { label:'Envoyé',    color:'var(--teal)',   bg:'rgba(0,210,180,0.1)',   icon:<CheckCircle2 size={12}/> },
  failed:  { label:'Échec',     color:'var(--red)',    bg:'rgba(255,77,109,0.1)',  icon:<XCircle size={12}/> },
  pending: { label:'En attente',color:'var(--amber)',  bg:'rgba(245,166,35,0.1)', icon:<Clock size={12}/> },
};

// ── Onglet Templates ─────────────────────────────────────────
function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [editing,   setEditing]   = useState({});
  const [saving,    setSaving]    = useState(null);
  const [expanded,  setExpanded]  = useState(null);

  useEffect(() => {
    notifs.templates().then(r => {
      setTemplates(r.data.templates || []);
      const e = {};
      (r.data.templates||[]).forEach(t => e[t.id] = { subject: t.subject, body: t.body, isActive: t.is_active });
      setEditing(e);
      if (r.data.templates?.[0]) setExpanded(r.data.templates[0].id);
    }).catch(() => toast.error('Erreur chargement templates'));
  }, []);

  const save = async (tpl) => {
    setSaving(tpl.id);
    try {
      await notifs.updateTemplate(tpl.id, editing[tpl.id]);
      toast.success(`Template "${tpl.label}" sauvegardé`);
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(null); }
  };

  const insertVar = (tplId, varName) => {
    setEditing(e => ({ ...e, [tplId]: { ...e[tplId], body: (e[tplId]?.body||'') + varName } }));
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      {templates.map(tpl => (
        <div key={tpl.id} className="card" style={{ padding:0, overflow:'hidden' }}>
          {/* Header */}
          <div onClick={() => setExpanded(expanded === tpl.id ? null : tpl.id)}
            style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 18px',
              cursor:'pointer', userSelect:'none' }}>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:14 }}>{tpl.label}</div>
              <div style={{ fontSize:12, color:'var(--text-3)', marginTop:2 }}>
                Code : <code style={{ fontSize:11 }}>{tpl.code}</code>
                {tpl.send_delay_hours < 0 && (
                  <span style={{ marginLeft:10, color:'var(--accent-light)' }}>
                    Envoi J{tpl.send_delay_hours}
                  </span>
                )}
              </div>
            </div>
            <div onClick={e => { e.stopPropagation(); setEditing(ed => ({ ...ed, [tpl.id]: { ...ed[tpl.id], isActive: !ed[tpl.id]?.isActive } })); }}
              style={{ width:40, height:22, borderRadius:11, cursor:'pointer',
                background: editing[tpl.id]?.isActive ? 'var(--teal)' : 'var(--bg-base)',
                border:'1px solid var(--border)', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
              <div style={{ position:'absolute', top:3, left: editing[tpl.id]?.isActive ? 20 : 3,
                width:14, height:14, borderRadius:'50%', background:'#fff',
                transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }}/>
            </div>
            <span style={{ fontSize:18, opacity:0.4 }}>{expanded === tpl.id ? '▲' : '▼'}</span>
          </div>

          {/* Body éditeur */}
          {expanded === tpl.id && (
            <div style={{ padding:'0 18px 18px', borderTop:'1px solid var(--border)' }}>
              <div className="form-group" style={{ marginTop:14 }}>
                <label className="form-label">Sujet</label>
                <input className="input" value={editing[tpl.id]?.subject||''}
                  onChange={e => setEditing(ed => ({ ...ed, [tpl.id]: { ...ed[tpl.id], subject: e.target.value } }))}/>
              </div>

              <div className="form-group">
                <label className="form-label">Corps du message</label>
                <textarea className="input" rows={10}
                  style={{ resize:'vertical', fontFamily:'monospace', fontSize:12, lineHeight:1.6 }}
                  value={editing[tpl.id]?.body||''}
                  onChange={e => setEditing(ed => ({ ...ed, [tpl.id]: { ...ed[tpl.id], body: e.target.value } }))}/>
              </div>

              {/* Variables */}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:12, color:'var(--text-3)', marginBottom:6 }}>
                  Variables disponibles — cliquez pour insérer :
                </div>
                <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                  {VARS.map(v => (
                    <button key={v} onClick={() => insertVar(tpl.id, v)}
                      style={{ padding:'3px 8px', borderRadius:6, border:'1px solid var(--border)',
                        background:'var(--bg-elevated)', cursor:'pointer', fontFamily:'monospace',
                        fontSize:11, color:'var(--accent-light)' }}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              <button className="btn btn-primary btn-sm" onClick={() => save(tpl)} disabled={saving === tpl.id}>
                {saving === tpl.id
                  ? <span className="spinner" style={{width:12,height:12}}/>
                  : <><Save size={12}/> Sauvegarder</>}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Onglet Test SMTP ─────────────────────────────────────────
function SmtpTab() {
  const [testEmail, setTestEmail] = useState('');
  const [testing,   setTesting]   = useState(false);
  const [result,    setResult]    = useState(null); // { success, message/error }

  const test = async () => {
    setTesting(true);
    setResult(null);
    try {
      const r = await notifs.testSmtp({ testEmail: testEmail || null });
      setResult({ success: true, message: r.data.message });
      toast.success(r.data.message);
    } catch(e) {
      const msg = e.response?.data?.error || e.message || 'Erreur inconnue';
      setResult({ success: false, error: msg });
      toast.error('Échec SMTP');
    } finally { setTesting(false); }
  };

  return (
    <div>
      <div className="card" style={{ maxWidth:520 }}>
        <div className="card-title" style={{ marginBottom:16 }}>
          <Wifi size={15} style={{ display:'inline', marginRight:6 }}/>
          Test de connexion SMTP
        </div>
        <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:16 }}>
          Vérifiez que la configuration SMTP (paramètres smtp_host, smtp_port, smtp_user, smtp_pass)
          est correcte. Vous pouvez aussi envoyer un email de test.
        </p>

        <div className="form-group">
          <label className="form-label">Email de test (optionnel)</label>
          <input className="input" type="email" value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder="votre@email.fr"/>
          <div style={{ fontSize:11, color:'var(--text-3)', marginTop:4 }}>
            Si renseigné, un email de test sera envoyé à cette adresse
          </div>
        </div>

        <button className="btn btn-primary" onClick={test} disabled={testing}>
          {testing
            ? <><span className="spinner" style={{width:14,height:14}}/> Test en cours…</>
            : <><Wifi size={14}/> {testEmail ? 'Tester et envoyer' : 'Tester la connexion'}</>}
        </button>

        {result && (
          <div style={{ marginTop:16, padding:'12px 14px', borderRadius:10,
            background: result.success ? 'rgba(0,210,180,0.08)' : 'rgba(255,77,109,0.08)',
            border: `1px solid ${result.success ? 'var(--teal)' : 'var(--red)'}`,
            display:'flex', alignItems:'flex-start', gap:10 }}>
            {result.success
              ? <CheckCircle2 size={16} color="var(--teal)" style={{ flexShrink:0, marginTop:1 }}/>
              : <XCircle size={16} color="var(--red)" style={{ flexShrink:0, marginTop:1 }}/>}
            <div>
              <div style={{ fontWeight:600, fontSize:13,
                color: result.success ? 'var(--teal)' : 'var(--red)' }}>
                {result.success ? 'Connexion réussie' : 'Connexion échouée'}
              </div>
              <div style={{ fontSize:12, color:'var(--text-2)', marginTop:3 }}>
                {result.message || result.error}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop:16, maxWidth:520,
        background:'rgba(124,92,252,0.04)', borderColor:'rgba(124,92,252,0.2)' }}>
        <div style={{ fontSize:13, color:'var(--text-2)', lineHeight:1.7 }}>
          <strong>Clés de configuration SMTP</strong> (dans Paramètres → Notifications) :
          <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:3 }}>
            {[
              ['smtp_host', 'Serveur SMTP (ex: smtp.gmail.com)'],
              ['smtp_port', 'Port (587 pour TLS, 465 pour SSL)'],
              ['smtp_user', 'Identifiant / email'],
              ['smtp_pass', 'Mot de passe ou App Password'],
              ['smtp_from', 'Nom affiché (ex: Mon Escape Game <contact@...>)'],
              ['smtp_secure', 'true si port 465, false sinon'],
            ].map(([k, desc]) => (
              <div key={k} style={{ display:'flex', gap:8, fontSize:12 }}>
                <code style={{ minWidth:90, color:'var(--accent-light)', flexShrink:0 }}>{k}</code>
                <span style={{ color:'var(--text-3)' }}>{desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Onglet Historique ────────────────────────────────────────
function HistoryTab() {
  const [notifList, setNotifList] = useState([]);
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState('');
  const [expanded,  setExpanded]  = useState(null);
  const PAGE = 30;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await notifs.list({ status: filter || undefined, limit: PAGE });
      setNotifList(r.data.notifications || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Erreur chargement historique'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      {/* Filtres */}
      <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'center' }}>
        {[['','Tous'], ['sent','Envoyés'], ['failed','Échecs'], ['pending','En attente']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`btn btn-sm ${filter===v?'btn-primary':'btn-secondary'}`}>
            {l}
          </button>
        ))}
        <span style={{ marginLeft:'auto', fontSize:12, color:'var(--text-3)' }}>
          {total} notification{total>1?'s':''}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
          <RefreshCw size={12} style={{ animation: loading?'spin 1s linear infinite':'none' }}/>
        </button>
      </div>

      {loading ? (
        <div className="empty-state"><span className="spinner"/></div>
      ) : notifList.length === 0 ? (
        <div className="empty-state card"><p>Aucune notification</p></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Réservation</th>
                <th>Destinataire</th>
                <th>Déclenché par</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {notifList.map(n => {
                const conf = STATUS_CONFIG[n.status] || STATUS_CONFIG.pending;
                return (
                  <>
                    <tr key={n.id} style={{ cursor:'pointer' }}
                      onClick={() => setExpanded(expanded === n.id ? null : n.id)}>
                      <td style={{ fontSize:12, color:'var(--text-3)', whiteSpace:'nowrap' }}>
                        {n.created_at
                          ? format(new Date(n.created_at), 'dd/MM/yyyy HH:mm', { locale:fr })
                          : '—'}
                      </td>
                      <td>
                        <code style={{ fontSize:11, color:'var(--accent-light)' }}>
                          {n.template_code}
                        </code>
                      </td>
                      <td>
                        {n.booking_ref
                          ? <span style={{ fontFamily:'monospace', fontWeight:700, fontSize:12 }}>{n.booking_ref}</span>
                          : <span style={{ color:'var(--text-3)' }}>—</span>}
                      </td>
                      <td style={{ fontSize:12 }}>{n.recipient || '—'}</td>
                      <td style={{ fontSize:12, color:'var(--text-3)' }}>
                        {n.triggered_by_first
                          ? `${n.triggered_by_first} ${n.triggered_by_last}`
                          : <span style={{ fontStyle:'italic' }}>Automatique</span>}
                      </td>
                      <td>
                        <span style={{ display:'inline-flex', alignItems:'center', gap:4,
                          padding:'3px 8px', borderRadius:99, fontSize:11, fontWeight:600,
                          background: conf.bg, color: conf.color }}>
                          {conf.icon} {conf.label}
                        </span>
                      </td>
                    </tr>
                    {expanded === n.id && (
                      <tr key={n.id + '_detail'}>
                        <td colSpan={6} style={{ padding:'0 16px 12px', background:'var(--bg-elevated)' }}>
                          <div style={{ padding:12, borderRadius:8, background:'var(--bg-base)', marginTop:4 }}>
                            <div style={{ fontSize:12, fontWeight:700, marginBottom:4 }}>
                              Sujet : {n.subject}
                            </div>
                            <pre style={{ fontSize:11, color:'var(--text-2)', margin:0,
                              whiteSpace:'pre-wrap', fontFamily:'monospace', lineHeight:1.6 }}>
                              {n.body_rendered || n.body}
                            </pre>
                            {n.error_msg && (
                              <div style={{ marginTop:8, fontSize:11, color:'var(--red)',
                                background:'rgba(255,77,109,0.08)', padding:'6px 10px', borderRadius:6 }}>
                                ✗ {n.error_msg}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Page principale ──────────────────────────────────────────
export function AdminNotifications() {
  const { can } = useAuth();
  if (!can('notifications.manage')) return (
    <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>
  );

  const [tab, setTab] = useState('templates');

  const TABS = [
    { id:'templates', label:'Templates' },
    { id:'smtp',      label:'Test SMTP' },
    { id:'history',   label:'Historique' },
  ];

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Notifications</h2>
          <p>Templates de messages automatiques et historique des envois</p>
        </div>
      </div>
      <div className="page-body">
        <div style={{ display:'flex', gap:6, marginBottom:20,
          borderBottom:'1px solid var(--border)', paddingBottom:12 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding:'7px 16px', borderRadius:8, border:'none', cursor:'pointer',
                fontFamily:'inherit', fontSize:13, fontWeight:600,
                background: tab===t.id ? 'var(--accent)' : 'var(--bg-elevated)',
                color: tab===t.id ? '#fff' : 'var(--text-2)',
                transition:'all 0.12s' }}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'templates' && <TemplatesTab/>}
        {tab === 'smtp'      && <SmtpTab/>}
        {tab === 'history'   && <HistoryTab/>}
      </div>
    </>
  );
}

export default AdminNotifications;
